/**
 * @file Turns My tasks' forms and their current versions into rows, in the
 * query's order. Consumer: `useHomeMyTasks`. Project is the version's
 * reference under the Home forms table's primary entity key, state and due
 * come from the current version, and a form whose version has not arrived
 * reads null with no due date. Due reads `notConfigured` while the project
 * has no form due-date field, and `set` only for a Firestore `Timestamp`,
 * relative to the calendar day of `now` and late only before that day. A row
 * opens the project detail when its form has a project, analytics is
 * configured and the reader can read it, at the scope and period the Analytics
 * page defaults to.
 */
import { Timestamp } from 'firebase/firestore';
import { DateTime } from 'luxon';

import { homePageConfig, type FieldId } from '#types';
import {
  analyticsView,
  analyticsViewSearch,
  type AnalyticsRights,
} from '@/analytics';

import type {
  HomeFormDue,
  HomeMyTask,
  HomeMyTaskEntry,
  HomeMyTaskOpens,
} from './HomeMyTasks.types';

type HomeMyTasksInput = {
  entries: readonly HomeMyTaskEntry[];
  dueField: FieldId | null;
  rights: Pick<AnalyticsRights, 'canRead' | 'canReadOrg'>;
  analyticsConfigured: boolean;
  now: DateTime;
};

const LOCALE = 'en-US';

const dueOf = (value: unknown, now: DateTime): HomeFormDue => {
  if (!(value instanceof Timestamp)) return { status: 'none' };
  const day = DateTime.fromJSDate(value.toDate(), { zone: now.zone })
    .setLocale(LOCALE)
    .startOf('day');
  const daysLate = Math.round(now.startOf('day').diff(day, 'days').days);
  if (daysLate === 0) return { status: 'set', text: 'today', late: false };
  if (daysLate < 0)
    return { status: 'set', text: day.toFormat('LLL dd'), late: false };
  return {
    status: 'set',
    text: daysLate === 1 ? '1 day ago' : `${daysLate} days ago`,
    late: true,
  };
};

const opensOf = (
  formId: string,
  projectId: string | undefined,
  rights: HomeMyTasksInput['rights'],
  analyticsConfigured: boolean,
): HomeMyTaskOpens =>
  projectId !== undefined && analyticsConfigured && rights.canRead
    ? {
        target: 'project',
        to: `/analytics?${analyticsViewSearch(
          analyticsView(
            new URLSearchParams([
              ['tab', 'projects'],
              ['project', projectId],
            ]),
            rights,
          ),
        )}`,
      }
    : { target: 'form', to: `/forms/${formId}` };

export const homeMyTasks = ({
  entries,
  dueField,
  rights,
  analyticsConfigured,
  now,
}: HomeMyTasksInput): HomeMyTask[] => {
  const projectKey = homePageConfig.formsTable.primaryEntityReferenceKey;
  return entries.map(({ formId, form, version }) => {
    const reference =
      projectKey === undefined
        ? undefined
        : version?.entityReferences?.[projectKey];
    return {
      formId,
      formNumber: form.formNumber ?? null,
      project: reference?.entityRecordName ?? null,
      state: version?.state ?? null,
      due:
        dueField === null
          ? { status: 'notConfigured' }
          : dueOf(version?.fields[dueField]?.value, now),
      opens: opensOf(
        formId,
        reference?.entityRecordId,
        rights,
        analyticsConfigured,
      ),
    };
  });
};
